// ** React Imports
import React, { Suspense, lazy } from 'react'
import { BrowserRouter } from 'react-router-dom'

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

// ** Toast
import { Toaster } from 'react-hot-toast'

// ** i18n
import './configs/i18n'

// ** PrismJS
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import 'prismjs/components/prism-jsx.min'
import 'bootstrap/dist/css/bootstrap.min.css'
import './index.scss'
import '@fortawesome/fontawesome-free/css/all.min.css'

// ** Core styles
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AbilityContext, ability } from './utility/ability'

// ** App import
const LazyApp = lazy(() => import('./App'))

import { createRoot } from 'react-dom/client'
import { IOCProvider, container } from './ioc.react'
import themeConfig from './configs/themeConfig'
import { AppProvider } from './contexts/AppContext'
import LoaderBall from '@components/loading-custom'
import { LocalizationProvider } from '@mui/x-date-pickers'
import GlobalLoader from './components/global-loader'

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 1000 * 60 * 5 } }
})

const wrapContainer = document.getElementById('root')!
const root = createRoot(wrapContainer)

if (!(crypto as any).randomUUID) {
  ; (crypto as any).randomUUID = function () {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }
}

const AppView: React.FC = () => {
  return (
    <>
      <IOCProvider container={container}>
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <GlobalLoader />
            <AppProvider>
              <AbilityContext.Provider value={ability}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Suspense fallback={<LoaderBall />}>
                    <LazyApp />
                    <Toaster
                      position={themeConfig.layout.toastPosition}
                      toastOptions={{ className: 'react-hot-toast' }}
                    />
                  </Suspense>
                </LocalizationProvider>
              </AbilityContext.Provider>
            </AppProvider>
          </QueryClientProvider>
        </BrowserRouter>
      </IOCProvider>
    </>
  )
}

root.render(<AppView />)
